/**
 * iOS-Audio-Unlock.
 *
 * Auf iOS Safari startet jeder AudioContext im Zustand `suspended` und darf
 * erst dann Sound ausgeben, wenn er innerhalb eines User-Gestures fortgesetzt
 * wurde (`resume()` + Abspielen eines „echten" Buffers). Phaser versucht
 * diesen Trick automatisch beim ersten Tap auf das Canvas – aber bei unserem
 * Flow tippt der User vorher schon auf einen React-Button („Spiel starten").
 * Dieser Klick zählt als Geste – aber Phaser bekommt davon nichts mit, weil
 * der Sound-Manager zu dem Zeitpunkt noch gar nicht existiert.
 *
 * Lösung: Wir hängen uns global an die erste User-Interaktion, erzeugen
 * einen "Master"-AudioContext und entsperren ihn. Phaser kann dann später
 * denselben Trick auf seinem eigenen Context nochmal anwenden – aber selbst
 * wenn nicht, hat iOS dann schon "gehört", dass die Seite Audio darf.
 */

let masterContext: AudioContext | null = null;
let silentAudio: HTMLAudioElement | null = null;

function getCtor(): typeof AudioContext | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Master-Context für die ganze App. Wird beim ersten Tap erzeugt und
 * resumed, ist danach „warm" für alle weiteren Sound-Operationen.
 */
export function getMasterAudioContext(): AudioContext | null {
  if (masterContext) return masterContext;
  const Ctor = getCtor();
  if (!Ctor) return null;
  try {
    masterContext = new Ctor();
  } catch {
    return null;
  }
  return masterContext;
}

function playSilentBuffer(ctx: AudioContext) {
  // 1-Sample Silent-Buffer → reicht iOS als "echte" Wiedergabe.
  try {
    const buffer = ctx.createBuffer(1, 1, 22_050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    if (typeof source.start === "function") {
      source.start(0);
    } else {
      // Sehr alte Safari-Versionen
      (source as unknown as { noteOn?: (t: number) => void }).noteOn?.(0);
    }
  } catch {
    /* egal */
  }
}

/**
 * Sehr kleines, leeres MP3 (≈100 Byte) als Data-URL.
 * iOS Safari akzeptiert nur dann audio.play() innerhalb eines Gestures,
 * wenn das Audio-Element einen gültigen Source hat. Das hier ist absolute
 * Stille, aber gültiges MP3.
 */
const SILENT_MP3 =
  "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjQ1LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAEAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDA//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjkxAAAAAAAAAAAAAAAAJAAAAAAAAAAAASCBNzC9AAAAAAAAAAAAAAAAAAAAAP/7kGQAD/AAAGkAAAAIAAANIAAAAQAAAaQAAAAgAAA0gAAABExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

function ensureSilentAudio(): HTMLAudioElement | null {
  if (typeof document === "undefined") return null;
  if (silentAudio) return silentAudio;
  const el = document.createElement("audio");
  el.src = SILENT_MP3;
  el.loop = false;
  el.preload = "auto";
  el.style.display = "none";
  document.body.appendChild(el);
  silentAudio = el;
  return el;
}

/**
 * Versucht, alle Audio-Subsysteme zu entsperren. MUSS synchron innerhalb
 * einer echten User-Geste aufgerufen werden – `await` würde die Geste
 * brechen, deshalb feuern wir hier nur ohne zu warten.
 *
 * Zwei parallele Tricks:
 * 1. AudioContext.resume() + Silent-Buffer (für WebAudio/Phaser)
 * 2. HTMLAudioElement.play() auf einer Mini-MP3-Datei (für DOM-Audio
 *    und manche iOS-Versionen, die nur darauf reagieren)
 */
export function unlockAudioContexts(): void {
  const ctx = getMasterAudioContext();
  if (ctx) {
    try {
      // KEIN await – der Resume-Aufruf selbst gilt als Geste,
      // das fertige Promise nicht mehr.
      if (ctx.state === "suspended") {
        void ctx.resume();
      }
      playSilentBuffer(ctx);
    } catch {
      /* nächster Tap probiert's erneut */
    }
  }

  // HTML-Audio-Variante zusätzlich anstoßen
  const el = ensureSilentAudio();
  if (el) {
    try {
      el.muted = true;
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          /* ignorieren */
        });
      }
      el.pause();
      el.currentTime = 0;
      el.muted = false;
    } catch {
      /* nächster Tap probiert's erneut */
    }
  }
}

/**
 * Wird einmal beim App-Start aufgerufen. Hängt sich an Touch-/Click-/Keydown-
 * Events und entsperrt iOS-Audio beim ersten Mal. Anschließend deregistriert
 * sich der Listener selbst.
 */
export function installGlobalAudioUnlock(): void {
  if (typeof window === "undefined") return;
  // Bei jedem User-Input erneut versuchen zu entsperren. Wir entfernen den
  // Listener nicht – iOS kann den Context unter Umständen wieder
  // suspenden (z. B. wenn der Tab in den Hintergrund geht und zurück),
  // dann brauchen wir den nächsten Tap erneut.
  const handler = () => {
    unlockAudioContexts();
  };
  window.addEventListener("pointerdown", handler, { passive: true });
  window.addEventListener("touchstart", handler, { passive: true });
  window.addEventListener("keydown", handler);
}
